import {
  apiPlugin,
  storyblokInit,
  type ISbStoryData,
  useStoryblokBridge,
  type SbSDKOptions
} from '@storyblok/js';
import { onMount } from 'svelte';
import { env } from '$env/dynamic/public';

// return a cache value that is valid for the current minute
// meaning that we will have a cache hit for every minute
export const getStoryblokCacheValue = (version: 'draft' | 'published') => {
  if (version === 'draft') return Date.now();

  const now = new Date();
  now.setSeconds(0);
  now.setMilliseconds(0);
  return now.getTime();
};

export const getStoryblokLongCacheValue = (version: 'draft' | 'published') => {
  if (version === 'draft') return Date.now();

  const now = new Date();
  now.setSeconds(0);
  now.setMilliseconds(0);
  now.setMinutes(now.getMinutes() + 10);
  return now.getTime();
};

export const fetchStoryblok = async (
  path: string,
  options: Record<string, string | number> = {}
) => {
  const params = new URLSearchParams();
  Object.entries(options).forEach(([key, value]) => {
    params.append(key, value.toString());
  });

  params.append(
    'cache',
    {
      clear: 'auto',
      type: 'memory'
    }.toString()
  );

  params.append('per_page', '500');

  params.append('token', env.PUBLIC_STORYBLOK_TOKEN);

  const response = await fetch(`https://api.storyblok.com/v2/${path}?${params}`, {
    headers: {
      'Cache-Control': 'public, max-age=600, s-maxage=604800',
      'Content-Type': 'application/json',
      'Vercel-CDN-Cache-Control': 'no-cache'
    }
  });

  return response.json();
};

export const getStoryblok = (apiOptions: SbSDKOptions['apiOptions'] = {}) => {
  const { storyblokApi } = storyblokInit({
    accessToken: env.PUBLIC_STORYBLOK_TOKEN,
    use: [apiPlugin],
    apiOptions: {
      https: true,
      cache: {
        clear: 'auto',
        type: 'memory'
      },
      ...apiOptions
    }
  });

  return storyblokApi as NonNullable<ReturnType<typeof storyblokInit>['storyblokApi']>;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function startStoryblokBridge<T extends { story: ISbStoryData<any> }>(
  id: number,
  onNewStory: (newStory: T['story']) => void
) {
  onMount(async () => {
    getStoryblok();
    useStoryblokBridge(id, onNewStory);
  });
}
